import { type HTMLAttributes } from 'react'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { cn } from '@/utils/cn'

export type LoadingSize = 'sm' | 'md' | 'lg'
export type LoadingVariant = 'spinner' | 'dots' | 'pulse' | 'skeleton'

interface LoadingProps extends HTMLAttributes<HTMLDivElement> {
  size?: LoadingSize
  variant?: LoadingVariant
  text?: string
  fullScreen?: boolean
  overlay?: boolean
}

const sizeClasses: Record<LoadingSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
}

const textSizeClasses: Record<LoadingSize, string> = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
}

const SpinnerLoader = ({ size }: { size: LoadingSize }) => (
  <Loader2 className={cn('animate-spin text-marykay-pink', sizeClasses[size])} />
)

const DotsLoader = ({ size }: { size: LoadingSize }) => {
  const dotSize = size === 'sm' ? 'h-2 w-2' : size === 'md' ? 'h-3 w-3' : 'h-4 w-4'

  return (
    <div className="flex items-center gap-1.5">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className={cn('rounded-full bg-marykay-pink', dotSize)}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: i * 0.15,
          }}
        />
      ))}
    </div>
  )
}

const PulseLoader = ({ size }: { size: LoadingSize }) => (
  <motion.div
    className={cn('rounded-full bg-marykay-pink/30', sizeClasses[size])}
    animate={{
      scale: [1, 1.2, 1],
      opacity: [0.5, 1, 0.5],
    }}
    transition={{
      duration: 1.2,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  />
)

export const Loading = ({
  size = 'md',
  variant = 'spinner',
  text,
  fullScreen = false,
  overlay = false,
  className,
  ...props
}: LoadingProps) => {
  const LoaderComponent = {
    spinner: SpinnerLoader,
    dots: DotsLoader,
    pulse: PulseLoader,
    skeleton: SpinnerLoader,
  }[variant]

  const content = (
    <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
      <LoaderComponent size={size} />
      {text && (
        <p
          className={cn(
            'text-gray-600 dark:text-gray-400 font-medium',
            textSizeClasses[size]
          )}
        >
          {text}
        </p>
      )}
    </div>
  )

  if (fullScreen) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm"
        {...props}
      >
        {content}
      </div>
    )
  }

  if (overlay) {
    return (
      <div
        className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-inherit"
        {...props}
      >
        {content}
      </div>
    )
  }

  return <div {...props}>{content}</div>
}

// Skeleton component for content placeholders
interface SkeletonProps {
  width?: string | number
  height?: string | number
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full'
  className?: string
}

const roundedClasses = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
}

export const Skeleton = ({
  width,
  height,
  rounded = 'md',
  className,
}: SkeletonProps) => (
  <motion.div
    className={cn(
      'bg-gray-200 dark:bg-gray-700',
      roundedClasses[rounded],
      className
    )}
    style={{
      width,
      height,
    }}
    animate={{
      opacity: [0.5, 1, 0.5],
    }}
    transition={{
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  />
)

// Skeleton text line
export const SkeletonText = ({
  lines = 3,
  className,
}: {
  lines?: number
  className?: string
}) => (
  <div className={cn('space-y-2', className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        height={16}
        width={i === lines - 1 ? '60%' : '100%'}
        rounded="sm"
      />
    ))}
  </div>
)

// Skeleton card
export const SkeletonCard = ({ className }: { className?: string }) => (
  <div
    className={cn(
      'bg-white dark:bg-gray-800 rounded-card p-4 border border-gray-200 dark:border-gray-700',
      className
    )}
  >
    <div className="flex items-center gap-3 mb-4">
      <Skeleton width={40} height={40} rounded="full" />
      <div className="flex-1">
        <Skeleton height={16} width="60%" className="mb-2" />
        <Skeleton height={12} width="40%" />
      </div>
    </div>
    <SkeletonText lines={2} />
  </div>
)

export default Loading
